import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { chargeKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import NavTabs from '@dashboard/components/NavTabs';
import PaymentsChargesDetail from './scenes/PaymentsChargesDetail';
import PaymentsChargesDetailInvoices from './scenes/PaymentsChargesDetailInvoices';
import PaymentsChargesDetailEmailLogs from './scenes/PaymentsChargesDetailEmailLogs';
import PaymentsChargesDetailTransactions from './scenes/PaymentsChargesDetailTransactions';
import { PaymentStatusBadges } from '@/dashboard/components/PaymentStatusBadges';
import { TrackRecent } from '@dashboard/components/CommandPalette/TrackRecent';
import { OrderPageHeader, OrderPageSkeleton } from '@dashboard/components/OrderPage';
import { formatDateTime } from '@dashboard/lib/format-date';
import { getPageTitle } from '@dashboard/lib/pageTitle';

const getTabs = ({ base, chargeId, payment }) => [
  {
    name: 'Payment',
    href: `${base}`,
    render: props => <PaymentsChargesDetail {...props} chargeId={chargeId} payment={payment} />,
  },

  {
    name: 'Emails',
    href: `${base}/emails`,
    render: props => (
      <PaymentsChargesDetailEmailLogs {...props} chargeId={chargeId} payment={payment} />
    ),
  },
  {
    name: 'Invoices',
    href: `${base}/invoices`,
    render: props => (
      <PaymentsChargesDetailInvoices {...props} chargeId={chargeId} payment={payment} />
    ),
  },
  {
    name: 'Transactions',
    href: `${base}/transactions`,
    render: props => (
      <PaymentsChargesDetailTransactions {...props} chargeId={chargeId} payment={payment} />
    ),
  },
];

const PaymentsChargesDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const {
    url,
    params: { chargeId },
  } = useRouteMatch();
  const location = useLocation();

  const {
    data: payment,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: chargeKey(chargeId),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${chargeId}`, token),
  });

  React.useEffect(() => {
    if (payment?.seller) {
      setAdminGhostingSellerId(payment.seller);
    }
  }, [payment?.seller, setAdminGhostingSellerId]);

  if (isLoading) {
    return <OrderPageSkeleton tabs={4} />;
  }

  if (!payment || error) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ base: url, chargeId, payment, refetch });

  return (
    <Card variant="root" contentColumn>
      <TrackRecent
        type="payment"
        id={payment._id}
        label={getPageTitle(payment.page) || payment._id}
        subtitle={getPriceLabel({ amount: payment.amount, currency: payment.currency })}
        path={`/orders/payments/${payment._id}`}
      />
      <CardHeader variant="root">
        <OrderPageHeader
          title={payment.orderId ? `Order #${payment.orderId}` : 'Order'}
          badges={<PaymentStatusBadges payment={payment} />}
          date={formatDateTime(payment.createdAt)}
        ></OrderPageHeader>

        <NavTabs aria-label="Charge detail">
          {tabs.map(tab => (
            <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
              {tab.name}
            </NavTabs.Item>
          ))}
        </NavTabs>
      </CardHeader>

      <CardContent variant="compact">
        <Switch>
          {tabs.map(tab => (
            <Route key={tab.href} path={tab.href} render={tab.render} exact />
          ))}
        </Switch>
      </CardContent>
    </Card>
  );
};

export default PaymentsChargesDetailOverview;
